import React, { useEffect, useMemo, useState } from "react"
import { api } from "services/api"

import { useAuthToken } from "components/hooks/use-auth-token"
import { useBITSToken } from "components/hooks/web3/use-bits-token"
import { useNativeBalance } from "components/hooks/web3/use-native-balance"
import { useOILToken } from "components/hooks/web3/use-oil-token"
import { useRestoreConnection } from "components/hooks/web3/use-restore-connection"
import { useWallet } from "components/hooks/web3/use-wallet"

import { Balance } from "components/shared/game/balance"

import { formatBitsOil } from "utils/web3/format-bits-oil"
import { formatMatic } from "utils/web3/format-matic"

import { BalancePopup } from "./balance-popup"

export default function BalanceBlock({ className }: { className?: string }) {
  const { connected } = useWallet()
  const auth = useAuthToken()
  const { balance: bitsBalance } = useBITSToken()
  const { balance: oilBalance } = useOILToken()
  const { balance: maticBalance } = useNativeBalance()
  const [unclaimedBalance, setUnclaimedBalance] = useState<{ bits: number; oil: number } | null>()

  useRestoreConnection()

  useEffect(() => {
    if (auth?.token) {
      api.rewards.getRewards(auth?.token).then(response => {
        if (response) {
          const oil = response.pending_sum.find(item => item.type === "OIL")?.amount || 0
          const bits = response.pending_sum.find(item => item.type === "BIT")?.amount || 0

          setUnclaimedBalance({ oil, bits })
        } else {
          setUnclaimedBalance(null)
        }
      })
    }
  }, [auth?.token])

  const bits: number | undefined = useMemo(() => {
    if (auth?.token && connected) {
      return bitsBalance
    }

    return undefined
  }, [auth?.token, bitsBalance, connected])

  const oil: number | undefined = useMemo(() => {
    if (auth?.token && connected) {
      return oilBalance
    }
    return undefined
  }, [auth?.token, connected, oilBalance])

  return (
    <div className={`flex items-center justify-end gap-1 md:ml-0 lg:gap-4 mt-2 sm:mb-0 ${className}`}>
      <Balance token="MATIC" value={formatMatic(maticBalance, "...")} />
      <BalancePopup
        type="oils"
        value={formatBitsOil(oil, "...")}
        frozenValue={formatBitsOil(unclaimedBalance?.oil, "...")}
      >
        <Balance token="OIL" value={formatBitsOil(oil, "...")} />
      </BalancePopup>

      <BalancePopup
        type="bits"
        value={formatBitsOil(bits, "...")}
        frozenValue={formatBitsOil(unclaimedBalance?.bits, "...")}
      >
        <Balance token="BITS" value={formatBitsOil(bits, "...")} />
      </BalancePopup>
    </div>
  )
}
